import { useState, useEffect } from 'react'
import { Phone, MessageCircle, ShoppingCart, X } from 'lucide-react'
import { useLanguage, commonTranslations } from '@/hooks/useLanguage'
import { useConfig } from '@/hooks/useMenuData'
import { useCart } from '@/hooks/useCart'
import { getTextAlign } from '@/lib/rtl'
import { sanitizePhoneNumber } from '@/lib/formatPrice'
import type { Config } from '@/types'

// ============================================================================
// Types
// ============================================================================

interface StickyBarProps {
  onCartClick?: () => void
}

// ============================================================================
// Action Button Component
// ============================================================================

interface ActionButtonProps {
  icon: React.ReactNode
  label: string
  href?: string
  onClick?: () => void
  variant: 'primary' | 'secondary' | 'accent'
  isRTL: boolean
  badge?: number
}

function ActionButton({
  icon,
  label,
  href,
  onClick,
  variant,
  isRTL,
  badge
}: ActionButtonProps) {
  const [isPressed, setIsPressed] = useState(false)

  const variantStyles = {
    primary: 'bg-primary text-white hover:bg-primary/90',
    secondary: 'bg-secondary text-primary hover:bg-secondary/80',
    accent: 'bg-accent text-white hover:bg-accent/90'
  }

  const content = (
    <>
      <div className="relative">
        {icon}
        {badge !== undefined && badge > 0 && (
          <span
            key={badge}
            className={`absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-accent text-white rounded-full ring-2 ring-white animate-badge-pop ${
              badge > 9 ? 'px-1' : ''
            }`}
          >
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <span className="truncate text-sm font-semibold">{label}</span>
    </>
  )

  const sharedClasses = `
    flex-1 h-12 min-w-0 flex items-center justify-center gap-2 px-3 rounded-xl whitespace-nowrap
    transition-all duration-200 touch-feedback
    ${variantStyles[variant]}
    ${isPressed ? 'scale-95' : ''}
  `

  if (href) {
    return (
      <a
        href={href}
        className={sharedClasses}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onMouseLeave={() => setIsPressed(false)}
        onTouchStart={() => setIsPressed(true)}
        onTouchEnd={() => setIsPressed(false)}
      >
        {content}
      </a>
    )
  }

  return (
    <button
      onClick={onClick}
      className={sharedClasses}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
    >
      {content}
    </button>
  )
}

// ============================================================================
// WhatsApp Button Component
// ============================================================================

interface WhatsAppButtonProps {
  whatsapp: string
  isRTL: boolean
}

function WhatsAppButton({ whatsapp, isRTL }: WhatsAppButtonProps) {
  const [isPressed, setIsPressed] = useState(false)

  return (
    <a
      href={`https://wa.me/${sanitizePhoneNumber(whatsapp)}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`
        flex-1 h-12 min-w-0 flex items-center justify-center gap-2 px-3 rounded-xl whitespace-nowrap
        text-sm font-semibold
        bg-[#25D366] text-white hover:bg-[#20BA5A]
        transition-all duration-200 touch-feedback
        ${isPressed ? 'scale-95' : ''}
      `}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      aria-label="Contact on WhatsApp"
    >
      <MessageCircle size={20} fill="currentColor" />
      <span className="truncate">WhatsApp</span>
    </a>
  )
}

// ============================================================================
// Sticky Bar Component
// ============================================================================

export function StickyBar({ onCartClick }: StickyBarProps) {
  const { language, isRTL, textDirection } = useLanguage()
  const config = useConfig()
  const { cart } = useCart()

  // Bottom action bar is always visible so call/order/cart are always reachable


  if (!config) return null

  const { contact, features } = config
  const cartFeature = features.cart

  // Don't show if no features are enabled
  if (!features.callButton && !features.whatsappOrdering && !cartFeature) {
    return null
  }

  const cartItemCount = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0

  // Get translations
  const t = {
    call: commonTranslations.call[language as 'fr' | 'ar' | 'en'] || commonTranslations.call.fr,
    cart: commonTranslations.cart[language as 'fr' | 'ar' | 'en'] || commonTranslations.cart.fr,
  }

  return (
    <>
      {/* Fixed Bottom Bar — always visible.
          It floats over the Footer, which reserves the bottom space itself
          (so there's no separate spacer strip beneath the footer). */}
      <div
        className={`
          fixed bottom-0 left-0 right-0 z-50
          bg-white/95 backdrop-blur-md border-t border-secondary/30
          shadow-[0_-2px_14px_rgba(44,24,16,0.06)] safe-bottom
        `}
        dir={textDirection}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className={`flex items-center gap-2 sm:gap-3 py-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Call Button */}
            {features.callButton && contact.phone && (
              <ActionButton
                icon={<Phone size={20} />}
                label={t.call}
                href={`tel:${contact.phone}`}
                variant="secondary"
                isRTL={isRTL}
              />
            )}

            {/* WhatsApp Button */}
            {features.whatsappOrdering && contact.whatsapp && (
              <WhatsAppButton whatsapp={contact.whatsapp} isRTL={isRTL} />
            )}

            {/* Cart Button */}
            {cartFeature && onCartClick && (
              <ActionButton
                icon={<ShoppingCart size={20} />}
                label={t.cart}
                onClick={onCartClick}
                variant={cartItemCount > 0 ? 'primary' : 'secondary'}
                isRTL={isRTL}
                badge={cartItemCount}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ============================================================================
// Compact Sticky Bar (for smaller screens)
// ============================================================================

interface CompactStickyBarProps {
  onCartClick?: () => void
}

export function CompactStickyBar({ onCartClick }: CompactStickyBarProps) {
  const { language, isRTL, textDirection } = useLanguage()
  const config = useConfig()
  const { cart } = useCart()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!config) return null

  const { contact, features } = config
  const cartFeature = features.cart

  const cartItemCount = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0

  return (
    <div
      className={`
        fixed bottom-4 left-4 right-4 z-50
        flex items-center gap-2 p-2
        bg-white/95 backdrop-blur-md rounded-2xl shadow-lg
        transition-all duration-300
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}
      `}
      dir={textDirection}
    >
      {/* Quick action buttons */}
      <div className={`flex items-center gap-2 flex-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {features.callButton && contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className="flex items-center justify-center w-12 h-12 rounded-xl bg-secondary/50 text-primary hover:bg-secondary transition-colors touch-feedback"
            aria-label="Call"
          >
            <Phone size={20} />
          </a>
        )}

        {features.whatsappOrdering && contact.whatsapp && (
          <a
            href={`https://wa.me/${sanitizePhoneNumber(contact.whatsapp)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 transition-colors touch-feedback"
            aria-label="WhatsApp"
          >
            <MessageCircle size={20} fill="currentColor" />
          </a>
        )}

        {cartFeature && onCartClick && (
          <button
            onClick={onCartClick}
            className={`
              flex-1 flex items-center justify-center gap-2 h-12 px-4 rounded-xl
              transition-colors touch-feedback
              ${cartItemCount > 0 ? 'bg-primary text-white' : 'bg-secondary/50 text-primary'}
            `}
          >
            <ShoppingCart size={20} />
            {cartItemCount > 0 && (
              <>
                <span className="font-medium">{cartItemCount}</span>
                <span className="text-sm">
                  {language === 'fr' ? 'articles' : language === 'ar' ? 'عناصر' : 'items'}
                </span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}
